import { useQuery } from '@tanstack/react-query';
import { Loader2, Share2 } from 'lucide-react';
import { apiRequest } from '@/lib/apiRequest';
import { wl } from './theme';

interface EntityRecord {
  entity_id: string;
  entity_type: string;
  name: string;
  resolution_keys: Record<string, string>;
}
interface TimelineEntry {
  observation_id: string;
  source_module: string;
  timestamp: string;
  name: string;
}
interface EntityTimeline {
  entries: TimelineEntry[];
  total: number;
}

/** Real data, simpler presentation than the mockup's node-link graph (that
 * visualization is Operators-tab scale — deferred with it). Shows the
 * profile's actual Entity Ontology node: its resolution keys and a recent
 * observation list pulled from the existing, already-shipped /entities
 * endpoints — nothing fabricated. */
export function EntityGraphPanel({ entityId }: { entityId: string }) {
  const entityQuery = useQuery({
    queryKey: ['watchlist-entity', entityId],
    queryFn: () => apiRequest<EntityRecord>(`/entities/${entityId}`),
  });
  const timelineQuery = useQuery({
    queryKey: ['watchlist-entity-timeline', entityId],
    queryFn: () => apiRequest<EntityTimeline>(`/entities/${entityId}/timeline?size=5`),
  });

  if (entityQuery.isLoading) return <Loader2 className="animate-spin" size={16} style={{ color: wl.green }} />;
  if (entityQuery.isError || !entityQuery.data) return null;

  const keys = Object.entries(entityQuery.data.resolution_keys || {});

  return (
    <div style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, borderRadius: 8, padding: '14px 16px', fontFamily: wl.font }}>
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={13} style={{ color: wl.green }} />
        <span style={{ color: wl.textMuted, fontSize: 10.5, letterSpacing: '0.08em', fontWeight: 700 }}>ENTITY GRAPH NODE</span>
      </div>
      <p style={{ color: wl.textFaint, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
        This profile is a node in the shared entity graph. Anything another module sees with the same identifiers merges here automatically.
      </p>
      {keys.length === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 11.5 }}>No resolution keys submitted yet.</p>
      ) : (
        <div className="flex flex-col gap-1.5 mb-3">
          {keys.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between">
              <span style={{ color: wl.textMuted, fontSize: 11 }}>{k}</span>
              <span style={{ color: wl.text, fontSize: 11 }}>{v}</span>
            </div>
          ))}
        </div>
      )}
      {timelineQuery.data && timelineQuery.data.total > 0 && (
        <div className="pt-2" style={{ borderTop: `1px solid ${wl.borderSoft}` }}>
          <span style={{ color: wl.textFaint, fontSize: 10 }}>{timelineQuery.data.total} observation(s) across other modules</span>
        </div>
      )}
    </div>
  );
}
